import { useEffect, useRef } from 'react';
import { useCommandPaletteContext } from '../CommandPaletteContext';

/**
 * Options for the useRegisterShortcut hook
 */
export interface UseRegisterShortcutOptions {
  /**
   * The keyboard shortcut to register (e.g., 'ctrl+shift+w')
   */
  shortcut: string;

  /**
   * The handler function to call when the shortcut is triggered
   */
  handler: () => void;

  /**
   * Human-readable label shown in the command palette shortcuts list.
   * When provided the shortcut appears in the footer popover.
   */
  label?: string;

  /**
   * Whether the shortcut is enabled. If false, the shortcut will not be registered.
   * @default true
   */
  enabled?: boolean;
}

/**
 * Hook for registering global keyboard shortcuts
 * 
 * This hook allows plugins to register keyboard shortcuts that work at the
 * root level (without needing to open the command palette first).
 * Shortcuts are automatically unregistered when the component unmounts.
 * 
 * @example
 * ```tsx
 * export const MyPlugin: React.FC = () => {
 *   const { open, navigateTo } = useCommandPaletteContext();
 *   
 *   const handleShortcut = useCallback(() => {
 *     open();
 *     navigateTo('my-plugin', 'my-page');
 *   }, [open, navigateTo]);
 *   
 *   useRegisterShortcut({
 *     shortcut: 'ctrl+shift+m',
 *     handler: handleShortcut,
 *   });
 *   
 *   return null;
 * };
 * ```
 */
export function useRegisterShortcut({
  shortcut,
  handler,
  label,
  enabled = true,
}: UseRegisterShortcutOptions): void {
  const { registerShortcut } = useCommandPaletteContext();
  const unregisterRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    if (!enabled) {
      return;
    }

    // Register the shortcut and store the cleanup function
    unregisterRef.current = registerShortcut(shortcut, handler, label);

    // Cleanup on unmount or when dependencies change
    return () => {
      if (unregisterRef.current) {
        unregisterRef.current();
        unregisterRef.current = null;
      }
    };
  }, [shortcut, handler, label, enabled, registerShortcut]);
}
